import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowUpRight, Sparkles, Search, PhoneCall, ExternalLink, Loader2, ImageOff, Filter } from 'lucide-react';
import PageShell from '@/components/PageShell';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useLang } from '@/lib/LangContext';
import {
  PartnerBrandLeadDialog,
  ConsultationLeadDialog,
} from '@/components/LeadForms';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Category names + ordering for the carousels at the top of the page.
// NOTE: 'specials' removed per client request — specials live under
// /special-offers/*, not under the catalogue brand grid.
const CATEGORY_ORDER = ['food', 'hygiene', 'vitamins', 'accessories', 'other'];
const CATEGORY_LABELS = {
  food:        { en: 'Food',                  ka: 'საკვები' },
  hygiene:     { en: 'Hygiene',               ka: 'ჰიგიენა' },
  vitamins:    { en: 'Vitamins & Additives',  ka: 'ვიტამინები და დანამატები' },
  accessories: { en: 'Accessories',           ka: 'აქსესუარები' },
  other:       { en: 'Other',                 ka: 'სხვა' },
};

const PICK = (obj, lang) => obj?.[lang] || obj?.en || '';

export default function CataloguePage() {
  const { lang, t } = useLang();
  const params = useParams();
  // When the URL is /catalogue/:category we restrict the brand grid to that
  // single category (sub-category click from Home). The action cards and footer
  // CTAs still render so the rest of the catalogue UX is unchanged.
  const categoryFilter = CATEGORY_ORDER.includes(params.category) ? params.category : null;

  const [partnerOpen, setPartnerOpen] = useState(false);
  const [consultOpen, setConsultOpen] = useState(false);

  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  // Legacy per-category search kept so existing refs still work.
  const [search, setSearch] = useState({});
  // New global search + category filter at the top of the page.
  const [globalSearch, setGlobalSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(categoryFilter || '__all__');

  const L = (en, ka) => (lang === 'ka' ? ka : en);

  // Pull live partner brand list (admin CRUD writes go here)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/partner-brands`);
        const data = await res.json();
        if (!cancelled) setBrands(data.items || []);
      } catch {
        if (!cancelled) setBrands([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Group brands by category, sorted by sort_order
  const grouped = useMemo(() => {
    const m = {};
    for (const b of brands) {
      const cat = b.category || 'other';
      (m[cat] = m[cat] || []).push(b);
    }
    Object.values(m).forEach((arr) =>
      arr.sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999)),
    );
    const effective = categoryFilter || (activeCategory !== '__all__' ? activeCategory : null);
    const order = effective ? [effective] : CATEGORY_ORDER;
    return order.filter((c) => m[c]?.length).map((c) => [c, m[c]]);
  }, [brands, categoryFilter, activeCategory]);

  // Apply global search across every category, then fallback to per-category search for compat.
  const filteredGrouped = useMemo(() => {
    const gq = globalSearch.trim().toLowerCase();
    return grouped
      .map(([catKey, items]) => {
        const q = (search[catKey] || '').trim().toLowerCase();
        let out = items;
        if (gq) {
          out = out.filter((b) => (b.name || '').toLowerCase().includes(gq) || (b.name_ka || '').toLowerCase().includes(gq));
        }
        if (q) {
          out = out.filter((b) => (b.name || '').toLowerCase().includes(q) || (b.name_ka || '').toLowerCase().includes(q));
        }
        return [catKey, out];
      })
      .filter(([, items]) => items.length > 0);
  }, [grouped, search, globalSearch]);

  // When filtering by category, prepend a small breadcrumb chip.
  const categoryHeading = categoryFilter
    ? PICK(CATEGORY_LABELS[categoryFilter], lang)
    : L('Brands included in the free package', 'უფასო პაკეტში შემავალი ბრენდები');

  return (
    <PageShell
      eyebrow={L('Partner brands', 'პარტნიორი ბრენდები')}
      title={L('50+ trusted brands at the same price', '50+ სანდო ბრენდი იმავე ფასად')}
      intro={L(
        'Food, hygiene and vitamins for your pet — from the same brands your pet store carries. Free delivery, no mark-ups.',
        'საკვები, ჰიგიენა და ვიტამინები შენი ცხოველისთვის — იმავე ბრენდებიდან, რასაც ზოომაღაზია ყიდის. უფასო მიწოდება, გარეშე ფასის დანამატი.',
      )}
    >
      {/* Global filter + search bar — single top-of-page control */}
      <section className="mb-6 md:mb-8" data-testid="catalogue-filter-bar">
        <div className="rounded-2xl bg-white border border-[#0A4D8C1A] p-3 md:p-4 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a94a2]" aria-hidden />
            <Input
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              placeholder={L('Search brand…', 'ძებნა ბრენდით…')}
              className="pl-9 rounded-xl h-11"
              data-testid="catalogue-global-search"
            />
          </div>
          <div className="md:w-56">
            <Select value={activeCategory} onValueChange={(v) => setActiveCategory(v)}>
              <SelectTrigger className="h-11 rounded-xl" data-testid="catalogue-category-filter">
                <div className="flex items-center gap-2">
                  <Filter size={14} />
                  <SelectValue />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{L('All categories', 'ყველა კატეგორია')}</SelectItem>
                {CATEGORY_ORDER.map((c) => (
                  <SelectItem key={c} value={c}>{PICK(CATEGORY_LABELS[c], lang)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Two action cards — compact horizontal layout above the brand grid */}
      <section className="mb-8 md:mb-10" data-testid="catalogue-actions-section">
        <div className="text-center max-w-2xl mx-auto mb-4 md:mb-5">
          <p className="text-xs tracking-[0.22em] uppercase font-bold text-[#F25C05]">
            {L('How would you like to order?', 'როგორ გსურს შეკვეთა?')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 max-w-5xl mx-auto">
          <ActionCard
            onClick={() => setPartnerOpen(true)}
            icon={<Sparkles size={20} />}
            accent="#F25C05"
            title={L('Learn the terms', 'გაიგე პირობები')}
            description={L(
              'Tell us which products you want and we will let you know which service package fits you best.',
              'გვითხარი რა პროდუქტები გსურთ და გეტყვით რომელი მომსახურების პაკეტით შეგიძლიათ ისარგებლოთ.',
            )}
            cta={L('Form', 'ფორმა')}
            testId="catalogue-action-partner-brand"
          />
          <ActionCard
            onClick={() => setConsultOpen(true)}
            icon={<PhoneCall size={20} />}
            accent="#0A4D8C"
            title={L('Book a Consultation', 'დაჯავშნე კონსულტაცია')}
            description={L(
              'Prefer to talk it through? Leave your number and our SmartPaw expert will call you back.',
              'გირჩევნია ვისაუბროთ? დატოვე შენი ნომერი და SmartPaw ექსპერტი დაგიკავშირდება.',
            )}
            cta={L('Request a callback', 'მოითხოვე ზარი')}
            testId="catalogue-action-consultation"
          />
        </div>
      </section>

      {/* Orange section heading + per-category brand sections with search */}
      <section data-testid="catalogue-partner-brands-section">
        {!categoryFilter && (
          <div className="mb-8 md:mb-10 flex items-end justify-between gap-4 flex-wrap">
            <h2
              className="font-display font-extrabold tracking-tight text-[#F25C05] text-3xl md:text-4xl"
              data-testid="catalogue-partner-brands-heading"
            >
              {categoryHeading}
            </h2>
          </div>
        )}
        {categoryFilter && (
          <div className="mb-4 flex justify-end">
            <Link
              to="/catalogue"
              className="text-sm font-bold text-[#0A4D8C] underline-offset-4 hover:underline"
              data-testid="catalogue-show-all-link"
            >
              {L('← Show all categories', '← ნახე ყველა კატეგორია')}
            </Link>
          </div>
        )}

        <div className="space-y-12 md:space-y-16">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-[#465B70]" data-testid="catalogue-brands-loading">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> {L('Loading brands…', 'ვტვირთავთ ბრენდებს…')}
            </div>
          ) : grouped.length === 0 ? (
            <div className="text-center py-12 text-[#465B70]" data-testid="catalogue-brands-empty">
              {L('No partner brands yet.', 'პარტნიორი ბრენდები ჯერ არ გვაქვს.')}
            </div>
          ) : (
            filteredGrouped.map(([categoryKey, items]) => {
              return (
                <div key={categoryKey} data-testid={`catalogue-category-${categoryKey}`}>
                  <div className="mb-5 flex items-end justify-between gap-3">
                    <h3 className="font-display text-2xl md:text-3xl font-bold text-[#05223D] tracking-tight">
                      {PICK(CATEGORY_LABELS[categoryKey] || { en: categoryKey, ka: categoryKey }, lang)}
                    </h3>
                    <span className="text-xs md:text-sm font-semibold text-[#465B70] bg-[#F5F2EB] px-3 py-1 rounded-full">
                      {items.length} {L('brands', 'ბრენდი')}
                    </span>
                  </div>
                  {items.length === 0 ? (
                    <p className="text-sm text-[#465B70] py-4" data-testid={`catalogue-${categoryKey}-no-results`}>
                      {L('No brands match your search.', 'ძიების შესაბამისი ბრენდი არ მოიძებნა.')}
                    </p>
                  ) : (
                    <ul
                      className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5"
                      data-testid={`catalogue-${categoryKey}-grid`}
                    >
                      {items.map((b) => {
                        const name = lang === 'ka' ? (b.name_ka || b.name) : b.name;
                        const hasLink = !!b.website_url;
                        const Cmp = hasLink ? 'a' : 'div';
                        return (
                          <li key={b.id} data-testid={`catalogue-brand-${b.id}`}>
                            <Cmp
                              {...(hasLink ? { href: b.website_url, target: '_blank', rel: 'noopener noreferrer' } : {})}
                              className={`group relative flex flex-col items-center justify-between gap-4 rounded-3xl bg-white p-5 md:p-6 aspect-square border border-[#0A4D8C1A] shadow-sm transition-all ${
                                hasLink ? 'hover:-translate-y-1 hover:border-[#F25C05] hover:shadow-[0_14px_38px_rgba(242,92,5,0.18)]' : ''
                              }`}
                              title={hasLink ? `${name} — opens in a new tab` : name}
                            >
                              {hasLink && (
                                <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#F5F2EB] text-[#465B70] group-hover:bg-[#F25C05] group-hover:text-white transition-colors" aria-hidden>
                                  <ExternalLink className="w-3 h-3" />
                                </span>
                              )}
                              {b.logo_url ? (
                                <div className="flex-1 w-full flex items-center justify-center">
                                  <img src={b.logo_url} alt={name} loading="lazy" className="max-h-20 md:max-h-24 max-w-full object-contain" />
                                </div>
                              ) : (
                                <div
                                  className="flex-1 flex h-16 md:h-20 w-16 md:w-20 flex-col items-center justify-center rounded-2xl border border-dashed border-[#0A4D8C33] bg-[#F5F2EB] text-[#465B70]"
                                  data-testid="brand-no-logo"
                                  aria-label="No logo available"
                                >
                                  <ImageOff className="h-6 w-6" strokeWidth={1.6} />
                                  <span className="mt-0.5 text-[8px] font-bold uppercase tracking-[0.12em] leading-none">
                                    {lang === 'ka' ? 'ლოგო' : 'No Logo'}
                                  </span>
                                </div>
                              )}
                              <span className="font-bold text-[#05223D] text-center text-xs md:text-sm leading-tight px-1 w-full truncate">
                                {name}
                              </span>
                            </Cmp>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })
          )}
        </div>
      </section>

      <PartnerBrandLeadDialog open={partnerOpen} onOpenChange={setPartnerOpen} />
      <ConsultationLeadDialog open={consultOpen} onOpenChange={setConsultOpen} />
    </PageShell>
  );
}

function ActionCard({ onClick, icon, accent, title, description, cta, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="group card-soft flex h-full flex-row items-center gap-4 p-4 md:p-5 text-left transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_44px_rgba(10,77,140,0.10)] focus-visible:-translate-y-1 focus-visible:shadow-[0_18px_44px_rgba(10,77,140,0.15)] focus-visible:outline-none"
    >
      <span
        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white"
        style={{ backgroundColor: accent }}
      >
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <h3 className="font-display text-base md:text-lg font-bold text-[#05223D] tracking-tight leading-tight">
          {title}
        </h3>
        <p className="mt-0.5 text-xs md:text-[13px] leading-snug text-[#465B70] line-clamp-2">
          {description}
        </p>
      </div>
      <span className="inline-flex shrink-0 items-center gap-1.5 font-bold text-[#0A4D8C] group-hover:text-[#F25C05] transition-colors text-sm whitespace-nowrap">
        <span className="hidden sm:inline">{cta}</span>
        <span className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-[#0A4D8C] transition-all group-hover:border-[#F25C05] group-hover:bg-[#F25C05] group-hover:text-white">
          <ArrowUpRight size={14} />
        </span>
      </span>
    </button>
  );
}
